import { useEffect, useId, useMemo, useRef } from 'react';
import { createError } from '@adv-frontend/error';
import type { AsyncFlow, AsyncFlowState } from '../../contract/async-flow';
import { isServer } from '../../utils/isServer';
import { AsyncFlowError, createAsyncFlowError } from '../../errors/asyncFlowError';
import { skipToken, type SkipToken } from '../skipToken';
import { useFlowInternal } from './useFlow';
import {
    useAsyncRenderSubscription,
    useAsyncRenderSubscriptionCleanup,
    useAsyncRenderSubscriptionOwner,
} from './useAsyncRenderSubscription';

const DEFAULT_ASYNC_RENDER_SUBSCRIPTION_TIMEOUT_MS = 60_000;

/**
 * Опции для хука {@link useAsyncFlow}.
 */
export interface UseAsyncFlowOptions {
    /**
     * Таймаут в миллисекундах для временной подписки, которая создается на время
     * между render и mount компонента (актуально для Suspense сценариев).
     * Отсчёт начинается после выхода потока из `pending`, чтобы выполняющийся запрос
     * не потерял подписку до получения результата; commit другого компонента pending-подписку
     * не освобождает. Независимо от состояния потока подписка живёт не дольше
     * `max(10 минут, keepRenderSubscriptionFor)` с момента последнего рендера.
     * Для общей подписки нескольких компонентов используется самый поздний deadline:
     * меньший timeout другого компонента не сокращает уже запрошенное время удержания.
     * Значение `0` отключает создание временной подписки.
     * @default 60000
     */
    keepRenderSubscriptionFor?: number;
}

/**
 * Ошибка вызова accessor'а из {@link useAsyncFlow} вне фазы синхронного рендера компонента.
 *
 * Accessor'ы (`()`, `current()`, `fresh()`) безопасны только в синхронном теле компонента.
 * Вне рендера они теряют семантику: брошенный Promise не активирует Suspense, а возвращаемые
 * данные читаются из самой свежей версии flow и могут быть рассинхронизированы с рендером.
 * Такие вызовы (из эффектов, обработчиков событий, промисов, таймаутов, других микротасок)
 * приводят к ошибке.
 */
export const AsyncFlowAccessorOutOfRenderError = createError<{ accessor: string }>('AsyncFlowAccessorOutOfRenderError');

interface StatusFlagsBase<T> {
    /**
     * Функция доступа к данным текущего flow. Бросает Promise на `loading` и `stale`,
     * возвращает данные на `refreshing`/`ready`. На `error` возвращает только `state.data`,
     * а при его отсутствии бросает ошибку.
     */
    current: () => T;
    /** Функция доступа к данным только в `ready` состоянии. Бросает Promise на любом pending. */
    fresh: () => T;
}

/**
 * Данные готовы, фоновых обновлений нет.
 */
export interface ReadyState<T> extends StatusFlagsBase<T> {
    /** Текущий статус хука. */
    status: 'ready';
    /** Указывает, является ли это начальной загрузкой данных. Всегда `false` для этого состояния. */
    isLoading: false;
    /** Указывает, отображаются ли устаревшие данные от предыдущего flow. Всегда `false` для этого состояния. */
    isStale: false;
    /** Указывает, идёт ли фоновое обновление данных. Всегда `false` для этого состояния. */
    isRefreshing: false;
    /** Указывает, готовы ли свежие данные. Всегда `true` для этого состояния. */
    isReady: true;
    /** Указывает, произошла ли ошибка. Всегда `false` для этого состояния. */
    isError: false;
    /** Указывает, выполняется ли асинхронная операция (загрузка/обновление/stale). Всегда `false` для этого состояния. */
    isPending: false;
    /** Произошедшая ошибка. Всегда `undefined` для этого состояния. */
    error: undefined;
}

/** Первичная загрузка: данных нет нигде (ни в текущем flow, ни от предыдущих flow). */
export interface LoadingState<T> extends StatusFlagsBase<T> {
    /** Текущий статус хука. */
    status: 'loading';
    /** Указывает, является ли это начальной загрузкой данных. Всегда `true` для этого состояния. */
    isLoading: true;
    /** Указывает, отображаются ли устаревшие данные от предыдущего flow. Всегда `false` для этого состояния. */
    isStale: false;
    /** Указывает, идёт ли фоновое обновление данных. Всегда `false` для этого состояния. */
    isRefreshing: false;
    /** Указывает, готовы ли свежие данные. Всегда `false` для этого состояния. */
    isReady: false;
    /** Указывает, произошла ли ошибка. Всегда `false` для этого состояния. */
    isError: false;
    /** Указывает, выполняется ли асинхронная операция (загрузка/обновление/stale). Всегда `true` для этого состояния. */
    isPending: true;
    /** Произошедшая ошибка. Всегда `undefined` для этого состояния. */
    error: undefined;
}

/** Поток сменился, в новом flow данных ещё нет, но отображаются устаревшие данные от предыдущего flow. */
export interface StaleState<T> extends StatusFlagsBase<T> {
    /** Текущий статус хука. */
    status: 'stale';
    /** Указывает, является ли это начальной загрузкой данных. Всегда `false` для этого состояния. */
    isLoading: false;
    /** Указывает, отображаются ли устаревшие данные от предыдущего flow. Всегда `true` для этого состояния. */
    isStale: true;
    /** Указывает, идёт ли фоновое обновление данных. Всегда `false` для этого состояния. */
    isRefreshing: false;
    /** Указывает, готовы ли свежие данные. Всегда `false` для этого состояния. */
    isReady: false;
    /** Указывает, произошла ли ошибка. Всегда `false` для этого состояния. */
    isError: false;
    /** Указывает, выполняется ли асинхронная операция (загрузка/обновление/stale). Всегда `true` для этого состояния. */
    isPending: true;
    /** Произошедшая ошибка. Всегда `undefined` для этого состояния. */
    error: undefined;
}

/** Тот же flow, есть актуальные данные, идёт фоновое обновление. */
export interface RefreshingState<T> extends StatusFlagsBase<T> {
    /** Текущий статус хука. */
    status: 'refreshing';
    /** Указывает, является ли это начальной загрузкой данных. Всегда `false` для этого состояния. */
    isLoading: false;
    /** Указывает, отображаются ли устаревшие данные от предыдущего flow. Всегда `false` для этого состояния. */
    isStale: false;
    /** Указывает, идёт ли фоновое обновление данных. Всегда `true` для этого состояния. */
    isRefreshing: true;
    /** Указывает, готовы ли свежие данные. Всегда `false` для этого состояния. */
    isReady: false;
    /** Указывает, произошла ли ошибка. Всегда `false` для этого состояния. */
    isError: false;
    /** Указывает, выполняется ли асинхронная операция (загрузка/обновление/stale). Всегда `true` для этого состояния. */
    isPending: true;
    /** Произошедшая ошибка. Всегда `undefined` для этого состояния. */
    error: undefined;
}

/** Загрузка завершилась ошибкой. */
export interface ErrorState<T> extends StatusFlagsBase<T> {
    /** Текущий статус хука. */
    status: 'error';
    /** Указывает, является ли это начальной загрузкой данных. Всегда `false` для этого состояния. */
    isLoading: false;
    /** Указывает, отображаются ли устаревшие данные от предыдущего flow. Всегда `false` для этого состояния. */
    isStale: false;
    /** Указывает, идёт ли фоновое обновление данных. Всегда `false` для этого состояния. */
    isRefreshing: false;
    /** Указывает, готовы ли свежие данные. Всегда `false` для этого состояния. */
    isReady: false;
    /** Указывает, произошла ли ошибка. Всегда `true` для этого состояния. */
    isError: true;
    /** Указывает, выполняется ли асинхронная операция (загрузка/обновление/stale). Всегда `false` для этого состояния. */
    isPending: false;
    /** Обертка `AsyncFlowError` над ошибкой загрузки с привязкой к async flow. */
    error: InstanceType<typeof AsyncFlowError>;
}

/**
 * Дискриминированный union возможных состояний хука.
 *
 * @typeParam T - Тип данных в async flow
 */
export type UseAsyncFlowState<T> = ReadyState<T> | LoadingState<T> | StaleState<T> | RefreshingState<T> | ErrorState<T>;

/**
 * Тип результата, возвращаемого {@link useAsyncFlow}.
 *
 * Результат — функция-accessor `(): T`, дополненная полями состояния и ещё двумя accessor'ами:
 * - `()` — дефолтный, в `pending` может вернуть данные предыдущего flow и бросает Promise
 *   только на `loading`. В `error` данные предыдущего flow не используются: accessor возвращает
 *   только `state.data`, а при его отсутствии бросает ошибку.
 * - `current()` — бросает Promise на `loading` + `stale`; на `refreshing` возвращает данные.
 * - `fresh()` — бросает Promise при любом обновлении данных, возвращает данные только в `ready`.
 *
 * @typeParam T - Тип данных
 */
export type UseAsyncFlowResult<T> = (() => T) & UseAsyncFlowState<T>;

/**
 * Подписывается на AsyncFlow и возвращает его состояние с поддержкой Suspense и ErrorBoundary.
 *
 * @typeParam T - Тип данных в AsyncFlow
 *
 * @param flow - AsyncFlow для подписки, или `skipToken` для пропуска подписки
 * @param options - Параметры подписки на поток
 * @returns Состояние асинхронной операции с тремя accessor'ами разной строгости
 *
 * @remarks
 * Как и {@link useFlow}, хук доставляет уведомления React в микротаске и схлопывает несколько
 * синхронных уведомлений в одно обновление с последним snapshot. Из-за этого `flushSync(() =>
 * flow.emit(...))` не обновляет компонент до возврата из `flushSync`. Синхронного режима у
 * React-хуков нет: он вернул бы риск обновления компонента во время чужого рендера.
 *
 * На `status === 'error'` accessor'ы бросают не саму ошибку из состояния потока, а обертку
 * `AsyncFlowError`: исходная ошибка лежит в ее `cause`, а рядом — поток и его снапшот на момент
 * броска, доступные через `getErrorFlows`. Пойманное разбирают по цепочке причин (`isCausedBy`,
 * `getErrorsCausedBy`, `getErrorFlows`), а не по верхнему уровню. Поле `error` результата
 * содержит ту же обертку, которую бросают accessor'ы, поэтому ручной `throw result.error`
 * сохраняет привязку к потоку и поддерживает автоматический сброс Error Boundary.
 *
 * Только на сервере при pending без данных (в том числе сохранённых от предыдущего flow)
 * хук принудительно выставляет `status='ready'`, `isLoading=false`, `isPending=false`,
 * чтобы guard'ы не обходили accessor: он бросает Promise и активирует серверный Suspense.
 *
 * Как и useFlow, хук всегда читает живое состояние. Паритет SSR обеспечивает поставщик
 * состояния, например modules-store, восстанавливающий Redux/RTK Query до hydrateRoot.
 * Во время гидрации действуют обычные клиентские статусы. Если accessor не может вернуть
 * данные, он бросает Promise; Suspense сохраняет серверную разметку до готовности потока.
 * Loading-guard, обходящий accessor, не приостанавливает гидрацию и требует паритета данных.
 *
 * @example
 * Дефолтный accessor — Suspense только при первичной загрузке:
 * ```tsx
 * function UserProfile({ userId }: { userId: string }) {
 *   const user = useAsyncFlow(userFlow(userId));
 *   return <div>{user().name}</div>;
 * }
 * ```
 *
 * @example
 * useQuery-like — Suspense при смене ключа, но не при фоновом обновлении:
 * ```tsx
 * function UserProfile({ userId }: { userId: string }) {
 *   const user = useAsyncFlow(userFlow(userId));
 *   return (
 *     <>
 *       {user.isRefreshing && <InlineSpinner/>}
 *       <div>{user.current().name}</div>
 *     </>
 *   );
 * }
 * ```
 *
 * @example
 * Строгий режим — Suspense при любом фоновом обновлении:
 * ```tsx
 * function UserProfile({ userId }: { userId: string }) {
 *   const user = useAsyncFlow(userFlow(userId));
 *   return <div>{user.fresh().name}</div>;
 * }
 * ```
 *
 * @example
 * Условная подписка через skipToken:
 * ```tsx
 * function UserProfile({ userId }: { userId: string | null }) {
 *   const user = useAsyncFlow(userId ? userFlow(userId) : skipToken);
 *   if (!user) return <div>Нет пользователя</div>;
 *   return <div>{user().name}</div>;
 * }
 * ```
 */
export function useAsyncFlow(flow: SkipToken, options?: UseAsyncFlowOptions): null;
export function useAsyncFlow<T>(flow: AsyncFlow<T>, options?: UseAsyncFlowOptions): UseAsyncFlowResult<T>;
export function useAsyncFlow<T>(
    flow: AsyncFlow<T> | SkipToken,
    options?: UseAsyncFlowOptions
): UseAsyncFlowResult<T> | null;
export function useAsyncFlow<T>(
    flow: AsyncFlow<T> | SkipToken,
    options?: UseAsyncFlowOptions
): UseAsyncFlowResult<T> | null {
    const flowId = useId();
    const renderSubscriptionOwner = useAsyncRenderSubscriptionOwner(flowId);
    // Отключаем стандартный useRenderSubscription внутри useFlow и подключаем вариант,
    // который не освобождает общую pending-подписку на commit другого компонента.
    const asyncRenderSubscriptionToken = useAsyncRenderSubscription(
        flow,
        options?.keepRenderSubscriptionFor ?? DEFAULT_ASYNC_RENDER_SUBSCRIPTION_TIMEOUT_MS,
        renderSubscriptionOwner
    );
    // Публичный useFlow не принимает AsyncFlow, поэтому подписываемся через useFlowInternal.
    const state = useFlowInternal<AsyncFlowState<T>>(flow, {
        ...options,
        keepRenderSubscriptionFor: 0,
    });
    const isSsr = isServer();
    const lastKnownDataRef = useRef<{ value: T } | null>(null);

    useAsyncRenderSubscriptionCleanup(flow, asyncRenderSubscriptionToken);

    useEffect(() => {
        if (flow !== skipToken && state && state.data !== undefined) {
            lastKnownDataRef.current = { value: state.data };
        }
    }, [flow, state]);

    // Базовые accessor'ы без guard'а кешируются в useMemo до момента смены flow/state — это
    // дает стабильную логику чтения данных. Сам guard и итоговая обертка пересоздаются на
    // каждом рендере, чтобы свежее замыкание видело актуальную render-фазу.
    const core = useMemo(() => {
        if (!state || flow === skipToken) {
            return null;
        }

        // Одна обертка на состояние нужна и accessor'ам, и ручному `throw result.error`.
        // Так оба способа проброса ошибки несут одинаковую ссылку на flow и его снапшот.
        const error = state.status === 'error' ? createAsyncFlowError(state.error, flow, state) : undefined;

        // Дефолтный accessor использует данные предыдущего flow только в pending-состоянии.
        // Ошибку нового flow они не скрывают: на error fallback возможен только из state.data.
        const coreReader = (): T => {
            if (state.status === 'pending') {
                if (state.data !== undefined) {
                    return state.data;
                }
                if (lastKnownDataRef.current !== null) {
                    return lastKnownDataRef.current.value;
                }
                throw flow.asPromise();
            }

            if (state.status === 'error') {
                if (state.data !== undefined) {
                    return state.data;
                }
                throw error;
            }

            return state.data;
        };

        // current: возвращает данные текущего flow. На refreshing вернёт data, на loading/stale — Suspense.
        const coreCurrent = (): T => {
            if (state.status === 'pending') {
                if (state.data !== undefined) {
                    return state.data;
                }
                throw flow.asPromise();
            }

            if (state.status === 'error') {
                if (state.data !== undefined) {
                    return state.data;
                }
                throw error;
            }

            return state.data;
        };

        // fresh: возвращает данные только в ready-состоянии. На любом pending — Suspense.
        // На error бросает ошибку независимо от наличия данных: если последний запрос завершился
        // ошибкой, имеющиеся данные устарели и не являются «свежими».
        const coreFresh = (): T => {
            if (state.status === 'pending') {
                throw flow.asPromise();
            }

            if (state.status === 'error') {
                throw error;
            }

            return state.data;
        };

        let status: UseAsyncFlowState<T>['status'];
        if (state.status === 'error') {
            status = 'error';
        } else if (state.status === 'success') {
            status = 'ready';
        } else if (state.data !== undefined) {
            status = 'refreshing';
        } else if (lastKnownDataRef.current !== null) {
            status = 'stale';
        } else if (isSsr) {
            // На SSR-рендере принудительно выставляем 'ready',
            // чтобы пользовательские guard'ы isLoading/isPending не отработали.
            // Без вызова accessor серверный Suspense не активируется, и in-flight запрос будет отброшен.
            // Внутри accessor'а state.status всё ещё 'pending', поэтому current()/fresh() бросят Promise
            // и серверный Suspense корректно дождётся данных.
            status = 'ready';
        } else {
            status = 'loading';
        }

        return {
            coreReader,
            coreCurrent,
            coreFresh,
            status,
            error,
        };
    }, [flow, state, isSsr]);

    // Маркер фазы синхронного рендера для guard'а accessor'ов. Поднимается на каждом вызове
    // хука, сбрасывается в микротаске сразу после завершения синхронной работы React (commit,
    // useLayoutEffect и т.п.). Любые отложенные вызовы accessor'ов (useEffect, обработчики
    // событий, Promise.then, setTimeout) увидят false и упадут с AsyncFlowAccessorOutOfRenderError.
    const isRenderPhaseRef = useRef(false);
    // eslint-disable-next-line react-hooks/refs -- запись из рендера намеренно, флаг управляет guard'ом accessor'ов
    isRenderPhaseRef.current = true;
    // eslint-disable-next-line react-hooks/refs -- сброс выполняется уже после рендера, в микротаске
    queueMicrotask(() => {
        isRenderPhaseRef.current = false;
    });

    // Guard оборачивает accessor проверкой, что он вызван синхронно в рендере. Контракт
    // accessor'ов завязан на Suspense/ErrorBoundary, которые работают только во время рендера
    // React, поэтому отложенный вызов гарантированно даст рассинхронизированный результат.
    const guard = useMemo(() => {
        return <R>(accessor: string, fn: () => R): (() => R) => {
            return () => {
                if (!isRenderPhaseRef.current) {
                    throw new AsyncFlowAccessorOutOfRenderError(
                        'useAsyncFlow accessor {accessor}() must be called synchronously during render',
                        {
                            cause: undefined,
                            additional: { accessor },
                        }
                    );
                }
                return fn();
            };
        };
    }, []);

    return useMemo(() => {
        // eslint-disable-next-line react-hooks/refs -- core хранит флаг текущей render-фазы; чтение здесь намеренное
        if (!core) {
            return null;
        }
        // eslint-disable-next-line react-hooks/refs -- core хранит флаг текущей render-фазы; чтение здесь намеренное
        const reader = guard('reader', core.coreReader);
        const result = buildState<T>(
            // eslint-disable-next-line react-hooks/refs -- core хранит флаг текущей render-фазы; чтение здесь намеренное
            core.status,
            // eslint-disable-next-line react-hooks/refs -- core хранит флаг текущей render-фазы; чтение здесь намеренное
            core.error,
            // eslint-disable-next-line react-hooks/refs -- core хранит флаг текущей render-фазы; чтение здесь намеренное
            guard('current', core.coreCurrent),
            // eslint-disable-next-line react-hooks/refs -- core хранит флаг текущей render-фазы; чтение здесь намеренное
            guard('fresh', core.coreFresh)
        );
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions,react-hooks/refs -- кастим к актуальной перегрузке
        return Object.assign(reader, result) as UseAsyncFlowResult<T>;
    }, [core, guard]);
}

function buildState<T>(
    status: UseAsyncFlowState<T>['status'],
    error: InstanceType<typeof AsyncFlowError> | undefined,
    current: () => T,
    fresh: () => T
): UseAsyncFlowState<T> {
    const base: StatusFlagsBase<T> = { current, fresh };

    switch (status) {
        case 'ready':
            return {
                ...base,
                status,
                isLoading: false,
                isStale: false,
                isRefreshing: false,
                isReady: true,
                isError: false,
                isPending: false,
                error: undefined,
            } satisfies ReadyState<T>;
        case 'loading':
            return {
                ...base,
                status,
                isLoading: true,
                isStale: false,
                isRefreshing: false,
                isReady: false,
                isError: false,
                isPending: true,
                error: undefined,
            } satisfies LoadingState<T>;
        case 'stale':
            return {
                ...base,
                status,
                isLoading: false,
                isStale: true,
                isRefreshing: false,
                isReady: false,
                isError: false,
                isPending: true,
                error: undefined,
            } satisfies StaleState<T>;
        case 'refreshing':
            return {
                ...base,
                status,
                isLoading: false,
                isStale: false,
                isRefreshing: true,
                isReady: false,
                isError: false,
                isPending: true,
                error: undefined,
            } satisfies RefreshingState<T>;
        case 'error':
            if (error === undefined) {
                throw new Error('Async flow error state must contain AsyncFlowError');
            }
            return {
                ...base,
                status,
                isLoading: false,
                isStale: false,
                isRefreshing: false,
                isReady: false,
                isError: true,
                isPending: false,
                error,
            } satisfies ErrorState<T>;
        default: {
            const _exhaustive: never = status;
            throw new Error(`Unknown status: ${String(_exhaustive)}`);
        }
    }
}
